/** Historical/default scores are not a person's confirmed assessment. In
 * particular, old residual score 7 has no likelihood × severity pair on a 1–5
 * scale: preserve the score rather than inventing factors or confirmation.
 */
import { z } from "zod";


const baselineFields = {
  hazardId: z.string().trim().min(1).max(200).optional(),
  likelihood: z.number().int().min(1).max(5),
  severity: z.number().int().min(1).max(5),
  residualScore: z.number().int().min(1).max(25),
  startingBand: z.string().trim().min(1).max(40).optional(),
  residualBand: z.string().trim().min(1).max(40).optional(),
};

export const RamsRiskBaselineSchema = z.discriminatedUnion("basis", [
  z.object({ ...baselineFields, basis: z.literal("historical_pdf"),
    residualDefaulted: z.literal(true).optional(),
    sourceVersionId: z.string().trim().min(1).max(200),
    sourcePdfSha256: z.string().regex(/^[a-f0-9]{64}$/),
  }).strict(),
  z.object({ ...baselineFields, basis: z.literal("historical_snapshot"),
    residualDefaulted: z.literal(true).optional(),
    sourceVersionId: z.string().trim().min(1).max(200),
    sourcePdfSha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  }).strict(),
  z.object({ ...baselineFields, basis: z.literal("library_default") }).strict(),
]);
export type RamsRiskBaseline = z.infer<typeof RamsRiskBaselineSchema>;
export const MAX_RAMS_RISK_BASELINES = 500;
export const MAX_RAMS_RISK_BASELINE_BYTES = 1024 * 1024;

const baselineKey = z.string().min(1).max(200).regex(/^[a-zA-Z0-9_-]+$/)
  .refine((key) => !["__proto__", "constructor", "prototype"].includes(key), "Invalid hazard identity.");

/** Check raw bounds before validating individual records. */
function baselineMapSchema<T extends z.ZodType>(baselineSchema: T) {
return z.preprocess((value, context) => {
  if (!value || typeof value !== "object" || Array.isArray(value) ||
      Object.keys(value).length > MAX_RAMS_RISK_BASELINES ||
      Object.keys(value).some((key) => ["__proto__", "constructor", "prototype"].includes(key)) ||
      new TextEncoder().encode(JSON.stringify(value)).byteLength > MAX_RAMS_RISK_BASELINE_BYTES) {
    context.addIssue({ code: "custom", message: "RAMS risk baselines exceed the document limit or are invalid." });
    return z.NEVER;
  }
  return value;
}, z.record(baselineKey, baselineSchema));
}
export const RamsRiskBaselinesSchema = baselineMapSchema(RamsRiskBaselineSchema);
export type RamsRiskBaselines = z.infer<typeof RamsRiskBaselinesSchema>;

export const RamsRiskBaselineReviewV1Schema = z.object({
  version:z.literal(1),sourceVersionId:z.string().min(1).max(200).nullable(),
  baselines:RamsRiskBaselinesSchema,
  taskHazardSlugs:z.array(baselineKey).max(MAX_RAMS_RISK_BASELINES).refine((slugs)=>new Set(slugs).size===slugs.length),
}).strict().superRefine((value,context)=>{
  if(new TextEncoder().encode(JSON.stringify(value)).byteLength>MAX_RAMS_RISK_BASELINE_BYTES)
    context.addIssue({code:"custom",message:"RAMS risk review exceeds the document limit."});
});
export type RamsRiskBaselineReviewV1 = z.infer<typeof RamsRiskBaselineReviewV1Schema>;

/** Installed readers accept additive response fields. Persisted snapshots,
 * requests and server egress retain the independent strict schemas above. */
const RamsRiskBaselineReadSchema = z.discriminatedUnion("basis", [
  RamsRiskBaselineSchema.options[0].strip(),
  RamsRiskBaselineSchema.options[1].strip(),
  RamsRiskBaselineSchema.options[2].strip(),
]);
export const RamsRiskBaselineReviewReadV1Schema = RamsRiskBaselineReviewV1Schema
  .safeExtend({ baselines: baselineMapSchema(RamsRiskBaselineReadSchema) }).strip();
export const RamsRiskBaselinePreconditionV1Schema = z.object({
  version:z.literal(1),sourceVersionId:z.string().min(1).max(200).nullable(),digest:z.string().regex(/^[a-f0-9]{64}$/),
}).strict();
export type RamsRiskBaselinePreconditionV1 = z.infer<typeof RamsRiskBaselinePreconditionV1Schema>;

/** Canonical digest validation; hashing and risk-band derivation live in logic. */
export const RamsRiskBaselineDigestScopeSchema=z.object({
  projectId:z.string().min(1).max(200),sourceVersionId:z.string().min(1).max(200).nullable(),fresh:z.boolean(),
}).strict();
export const RamsRiskBaselineCanonicalRowSchema=z.object({
  slug:baselineKey,likelihood:baselineFields.likelihood,severity:baselineFields.severity,residualScore:baselineFields.residualScore,
  startingBand:z.string().min(1).max(40),residualBand:z.string().min(1).max(40),
  basis:z.enum(["library_default","historical_pdf","historical_snapshot"]),residualDefaulted:z.boolean(),
}).strict();

/** Null distinguishes absent/corrupt history from an explicit empty snapshot. */
export function ramsRiskBaselinesFromPayload(payload: unknown): RamsRiskBaselines | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const parsed = RamsRiskBaselinesSchema.safeParse((payload as Record<string, unknown>).ramsRiskBaselinesSnapshot);
  return parsed.success ? parsed.data : null;
}

